import * as React from "react"
import { cn } from "./lib/cn"

/**
 * Quiet primary CTA — white surface, neutral text, layered brand-purple
 * glow that intensifies on hover. The icon (any direct SVG child) takes
 * brand purple as the action signifier.
 *
 * Sits between `<GlassCTAButton>` (loud, solid brand fill) and
 * `<GlassGhostButton>` (no static glow, only on hover) in the button
 * family — primary intent without the promotional weight of a brand fill.
 *
 * Where it shines:
 *   - Toolbar primary actions inside glass cards (Upload, Download)
 *   - Header CTAs in panels / lightboxes where the brand fill would
 *     compete with surrounding glass surfaces
 *   - Any "primary action that should still feel calm"
 *
 * Visual language matches the design system's other glow surfaces:
 *   - Active sidebar item (bg-white + brand glow)
 *   - Drive icon header (white pill + layered glow + 1px ring)
 *   - Sticky toolbar pinned shadow
 * The glow + 1px-ring stack defines the surface; hover carries hierarchy
 * through blur radius and alpha, not colour swap.
 *
 * Asymmetric padding (`pl-3 pr-3.5`) is recommended when the leading icon
 * makes the button look left-heavy — override the default `px-3.5` via
 * className. See ui-ux-designer skill §"Optical Centering".
 */
function GlassGlowButton({ className, ...props }: React.ComponentProps<"button">) {
  return (
    <button
      data-slot="glass-glow-button"
      className={cn(
        "inline-flex items-center gap-1.5",
        "rounded-full h-8 px-3.5 text-xs font-semibold transition-all",
        "bg-white text-[#091E42]",
        // Static layered glow — drop shadow + 1px brand ring define the surface.
        "shadow-[0_2px_8px_rgba(86,81,255,0.18),0_0_0_1px_rgba(86,81,255,0.15)]",
        // Hover by intensity (blur + alpha grow), not colour change.
        "hover:shadow-[0_4px_14px_rgba(86,81,255,0.30),0_0_0_1px_rgba(86,81,255,0.25)]",
        // Tactile press.
        "active:scale-[0.97]",
        "disabled:opacity-50 disabled:pointer-events-none disabled:shadow-none",
        // Direct-SVG-child colour: icon = primary-action signifier in brand purple.
        "[&>svg]:text-[#5651ff]",
        // Dark mode: tinted-glass surface, white text, slightly louder glow
        // so the brand carry is visible against the dark backdrop.
        "dark:bg-white/12 dark:text-white",
        "dark:shadow-[0_2px_8px_rgba(86,81,255,0.30),0_0_0_1px_rgba(86,81,255,0.25)]",
        "dark:hover:shadow-[0_4px_14px_rgba(86,81,255,0.40),0_0_0_1px_rgba(86,81,255,0.35)]",
        "dark:[&>svg]:text-[#5651ff]/80",
        className,
      )}
      {...props}
    />
  )
}

export { GlassGlowButton }
